import { Box, Grid } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';
import { useAuth } from '../../session/InternalAuthProvider';
import { getOrderPage } from '../../common/AccountUtils';
import { AccountData, CalendarDisplayItem, OrderData, OrderStatus } from '../../api';
import { getEmployeeNames } from '../CalendarUtils';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { OrderTopBar } from './OrderTopBar';
import { getTimeFormat } from '../../common/getDateAndTimeFormat';
import { CalendarBoxProvider, CalendarBoxTypography } from './CalendarBoxContext';
import { getOrderSchedulerConfig, validateOrderPageMask } from '../views/dragndrop/resourceValidationUtils';
import CheckinDialog from '../dialogs/CheckinDialog/CheckinDialog';
import { useOrders } from '../swr/useOrders';

type OrderBoxProps = {
  order: OrderData;
  css?: any;
  viewType: 'month' | 'day';
  openOrder: (event: any, order: OrderData) => void;
  shouldNotDragNDrop?: boolean;
  hideEmployees?: boolean;
};

const OrderBox = ({ order, css, viewType, openOrder, shouldNotDragNDrop, hideEmployees }: OrderBoxProps) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: order.id,
  });
  const { users, setShouldBlockSwiping, startDate, endDate } = useCalendar();

  const { updateItem: updateOrder } = useOrders({ startDate, endDate });
  const monthView = viewType === 'month';
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const orderPage = getOrderPage(account, order.order_page_id);
  const timeFormat = getTimeFormat(account || undefined);

  const [highlight, setHighlight] = useState(false);
  const [checkInDialogIsOpen, setCheckInDialogIsOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setShouldBlockSwiping(isDragging);
  }, [isDragging, setShouldBlockSwiping]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const queryOrderId = urlParams.get('entityid');
    if (queryOrderId && queryOrderId === order.id) {
      setHighlight(true);
      setTimeout(() => {
        setHighlight(false);
        urlParams.delete('entityid');
        window.history.replaceState(null, '', `${window.location.pathname}?${urlParams.toString()}`);
      }, 3000); // Duration of the gradient animation
    }
  }, [order.id]);

  const isOrderToday = () => {
    if (!order.date) {
      return false;
    }
    return moment(order.date).isSame(moment(), 'day');
  };

  const hasNoEmployees = order.employee_ids.length === 0;

  const getTimeText = () => {
    if (!order.time) {
      return '';
    }
    return `${moment.unix(order.time).tz('utc').format('H:mm')} - ${moment
      .unix(order.time + order.duration)
      .tz('utc')
      .format(timeFormat)}`;
  };

  const shouldShowResources =
    account?.shared_resources?.scheduler_configs?.length && account?.shared_resources?.scheduler_configs?.length > 0;

  const shouldHaveRedBorder =
    !shouldShowResources && (hasNoEmployees || order.status === OrderStatus.PAYMENT_LINK_GENERATED);
  const isMax60MinOrder = order.duration <= 3600;

  const getBorderStyle = () => {
    // Red border for critical issues (no employees, payment pending)
    if (shouldHaveRedBorder) {
      return '1px solid red';
    }

    // Orange border for manual overrides
    if (order.override_scheduler_config_id) {
      return '2px solid #ff9800';
    }

    // Check if order doesn't fit its expected mask
    if (order.resource_indexes && order.resource_indexes.length > 0) {
      const config = getOrderSchedulerConfig(order, account);
      if (config) {
        const maskValidation = validateOrderPageMask(order, order.resource_indexes, config, false);
        if (!maskValidation.isValid) {
          // Same orange border as override for mask violations
          return '2px solid #ff9800';
        }
      }
    }

    // Default border
    return '1px solid rgb(230, 230, 230)';
  };

  // Create complete style objects for each view type
  const getCompleteStyles = () => {
    const baseStyles = {
      backgroundColor: orderPage?.color || '#6577b3',
      color: 'white',
      borderRadius: '8px',
      cursor: 'pointer',
      overflow: 'hidden',
      fontSize: '12px',
      border: getBorderStyle(),
      ...((order.override_scheduler_config_id || getBorderStyle().includes('#ff9800')) && {
        boxShadow: '0 0 0 1px #ff9800 inset',
      }),
      ...(highlight && {
        animation: 'gradient-animation 3s ease-in-out',
        '@keyframes gradient-animation': {
          '0%': {
            background: 'linear-gradient(45deg, #2b324c, #67ad5b)',
          },
        },
      }),
    };

    if (monthView) {
      return {
        ...baseStyles,
        marginLeft: '8px',
        marginRight: '8px',
        marginBottom: '2px',
        '@media (max-width: 600px)': {
          marginLeft: 0,
          marginRight: 0,
        },
      };
    }

    // Day view
    const dayBaseStyles = {
      ...baseStyles,
      position: 'absolute' as const,
      width: `calc(${css.width}% - 2px)`,
      left: `${css.left}%`,
      top: `${css.top}px`,
      height: `${css.height - 2}px`,
      transition: isDragging ? 'none' : 'transform 200ms ease',
      zIndex: isDragging ? 1000 : 'auto',
      ...(transform && { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }),
    };

    return dayBaseStyles;
  };

  // Get content items based on configured display priorities
  const getContentItems = () => {
    const items = [];
    const priorities = account?.calendar_display_settings?.order_display_priorities || [
      CalendarDisplayItem.EMPLOYEES,
      CalendarDisplayItem.CUSTOMER_NAME,
      CalendarDisplayItem.ORDER_NUMBER,
    ];

    // Time (for month view)
    if (monthView) {
      items.push(
        <Grid key="time" dir="ltr" sx={{ textAlign: 'center' }}>
          <CalendarBoxTypography>{getTimeText()}</CalendarBoxTypography>
        </Grid>,
      );
    }

    const getMaxLines = () => {
      if (order.duration >= 7200) return 3;
      if (order.duration > 3600) return 2;
      return 1;
    };

    let displayCount = 0;
    const maxItems = isMax60MinOrder ? 2 : 4;
    const mt = isMax60MinOrder ? -0.5 : undefined;

    const isSingleItem = priorities.length === 1;
    const singleItemStyle = isSingleItem ? { fontSize: '16px' } : undefined;
    for (const item of priorities) {
      if (displayCount >= maxItems) break;

      switch (item) {
        case CalendarDisplayItem.EMPLOYEES:
          if (order.employee_ids.length > 0 && !hideEmployees) {
            items.push(
              <Grid
                mt={mt}
                width={'100%'}
                key="employees"
                container
                justifyContent="center"
                flexWrap="nowrap"
                alignItems="center"
                gap={0.5}>
                {order.employee_ids.length &&
                  getEmployeeNames(order.employee_ids, users).map((x) => (
                    <Grid key={`employee-${x}`} item border="0.5px solid black" px={1} borderRadius="8px">
                      <CalendarBoxTypography sx={singleItemStyle}>{x}</CalendarBoxTypography>
                    </Grid>
                  ))}
              </Grid>,
            );
            displayCount++;
          }
          break;

        case CalendarDisplayItem.CUSTOMER_NAME:
          if (!isMax60MinOrder || displayCount < 2) {
            items.push(
              <Grid key="name" width={'100%'} mt={mt}>
                <CalendarBoxTypography
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: getMaxLines(),
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    whiteSpace: 'normal',
                    textOverflow: 'unset',
                    ...singleItemStyle,
                  }}>
                  {`${order.firstname} ${order.lastname}`}
                </CalendarBoxTypography>
              </Grid>,
            );
            displayCount++;
          }
          break;

        case CalendarDisplayItem.ORDER_NUMBER:
          if (!isMax60MinOrder || displayCount < 2) {
            items.push(
              <Grid key="order-number" sx={{ textAlign: 'center' }} mt={mt}>
                <CalendarBoxTypography sx={singleItemStyle}>{`${order.order_number}#`}</CalendarBoxTypography>
              </Grid>,
            );
            displayCount++;
          }
          break;

        case CalendarDisplayItem.CHECKIN_TIME:
          if (order.checkin?.created_at && (!isMax60MinOrder || displayCount < 2)) {
            items.push(
              <Grid key="checkin-time" sx={{ textAlign: 'center' }} mt={mt}>
                <CalendarBoxTypography variant="caption" sx={singleItemStyle}>
                  {t('calendar.order_popper.checkin')}: {moment(order.checkin.created_at).format(timeFormat)}
                </CalendarBoxTypography>
              </Grid>,
            );
            displayCount++;
          }
          break;

        default:
          break;
      }
    }

    return items;
  };

  // Unified content renderer with consistent shell
  const renderContent = () => {
    const contentItems = getContentItems();

    return (
      <Grid container flexDirection="column">
        <OrderTopBar order={order} orderPage={orderPage} />
        <Grid container flexDirection="column" px={0.5} alignContent={'center'} mt={isMax60MinOrder ? -0.5 : undefined}>
          {contentItems}
        </Grid>
      </Grid>
    );
  };

  // Shared container with drag & drop and click handling
  const containerProps = monthView
    ? {}
    : {
        ref: shouldNotDragNDrop ? undefined : setNodeRef,
        ...(shouldNotDragNDrop ? {} : attributes),
        ...(shouldNotDragNDrop ? {} : listeners),
      };

  const handleClick = (event: any) => {
    if (monthView || !isDragging) {
      openOrder(event, order);
    }
  };

  const handleRightClick = (event: any) => {
    if (isOrderToday()) {
      event.preventDefault();
      setCheckInDialogIsOpen(true);
    }
  };

  const finalStyles = getCompleteStyles();

  return (
    <CalendarBoxProvider
      cssCalculatedWidth={css?.width}
      backgroundColor={orderPage?.color || '#6577b3'}
      viewType={viewType}>
      <Box {...containerProps} onClick={handleClick} onContextMenu={handleRightClick} sx={finalStyles}>
        <Grid
          container
          flexDirection="column"
          sx={{
            textAlign: 'center',
            wordWrap: 'break-word',
            height: '100%',
          }}>
          {renderContent()}
        </Grid>
      </Box>
      {checkInDialogIsOpen && (
        <CheckinDialog
          order={order}
          isOpen={checkInDialogIsOpen}
          setIsOpen={setCheckInDialogIsOpen}
          updateItem={updateOrder}
        />
      )}
    </CalendarBoxProvider>
  );
};

export default OrderBox;
